import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";

export class FormValidator {
    // Shared URL pattern: optional scheme, domain, any TLD length, optional port, optional path and query.
    // TLD allows {2,} (not {2,5}) so long TLDs like .agency/.online/.digital and tracking domains pass.
    private static readonly URL_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]{1,5})?(\/[^\s]*)?(\?[^\s]*)?$/i;

    /**
     * Provides a static method to generate a URL validator function for use with Angular forms.
     *
     * This method returns a `ValidatorFn` function that can be attached to a form control. It validates
     * the form control's value against a predefined URL pattern, ensuring the value conforms to a
     * typical URL structure. The URL validation supports both HTTP and HTTPS protocols, optional WWW
     * prefix, domain names with alphanumeric characters (including hyphens and periods), top-level
     * domains of 2 or more characters, optional port numbers, paths, and query strings.
     *
     * If the form control's value does not match the URL pattern, the validator returns an error object
     * with a key of `invalidUrl` set to true. Otherwise, if the value is valid or the control is empty,
     * it returns null, indicating no validation errors.
     *
     * Example usage:
     * ```
     * this.formControl = new FormControl('', [FormValidator.url()]);
     * ```
     */
    static url(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || typeof control.value !== "string" || control.value.trim() === "") {
                return null; // Si está vacío o no es válido, no hay error
            }

            if (!FormValidator.URL_PATTERN.test(control.value)) {
                return { invalidUrl: true }; // Retorna error si no coincide
            }

            return null; // URL válida
        };
    }
    static urlMultipleValues(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const listControlValues = control.value;

            // Verificar solo si control.value es un array
            if (Array.isArray(listControlValues)) {
                for (let i = 0; i < listControlValues.length; i++) {
                    const item = listControlValues[i];
                    if (item && !FormValidator.URL_PATTERN.test(item)) {
                        return { invalidUrl: true }; // Return error if URL is invalid
                    }
                }
            }

            return null; // Return null if there's no error
        };
    }

    static urlHttps(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            // Regex to ensure the URL starts with "https://", optionally includes "www.", and is followed by a valid domain name.
            const urlPattern =
                /^(https:\/\/www\.|https:\/\/)?[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
            if (control.value && !urlPattern.test(control.value)) {
                // Return error if URL is invalid
                return { invalidUrl: true };
            }
            return null; // Return null if there's no error
        };
    }

    /**
     * Static method that provides a custom validator for form controls in Angular to validate domain names.
     * This validator ensures that the control's value matches a domain name pattern specified by a regular expression.
     * If the control's value is empty, it returns null, indicating no error (field emptiness should be handled by another validator such as 'required').
     * If the control's value is non-empty, it checks the value against a regex pattern that matches standard domain name formats.
     *
     * Example valid domains: 'example.com', 'subdomain.example.co.uk'
     *
     * Returns:
     * - null if the value is empty or if it matches the domain regex pattern (indicating a valid domain).
     * - A validation error object with a key of 'invalidDomain' and the invalid value if the control's value does not match the domain regex pattern.
     *
     * Usage:
     * this.formControl = new FormControl('', [Validators.required, CustomValidators.domain()])
     *
     * @param control The form control instance associated with the input field.
     * @returns A ValidationErrors object if the validation fails (invalid domain), or null if validation passes (valid or empty domain).
     */
    static domain(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null; // Return null if the field is empty (consider using required validator separately)
            }

            // Expresión regular mejorada para aceptar dominios con TLD de cualquier longitud
            // También permite rutas opcionales (e.g., domain.com/path)
            const domainRegex = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;
            const valid = domainRegex.test(control.value);
            return valid ? null : { invalidDomain: { value: control.value } };
        };
    }

    /**
     * Static method that provides a custom validator for Angular form groups to enforce a dependency condition
     * between two form fields. This validator ensures that both fields must be either filled or empty simultaneously.
     * It is designed to be applied to a FormGroup that includes the two specified fields.
     *
     * This validator is useful for scenarios where the state of one field affects the validity of another field, such as
     * confirming that two email or password fields match or both fields need to be filled out to proceed.
     *
     * Returns:
     * - null if both fields satisfy the dependency condition (both are filled or both are empty).
     * - A validation error object with a key of 'fieldsDependency' if the condition is not met (one field is filled while the other is empty).
     *
     * Usage:
     * this.myFormGroup = new FormGroup({
     *   password: new FormControl(''),
     *   confirmPassword: new FormControl('')
     * }, { validators: CustomValidators.fieldsDependencyValidator('password', 'confirmPassword') });
     *
     * @param firstKey The key (form control name) of the first field in the form group.
     * @param secondKey The key (form control name) of the second field in the form group.
     * @returns A function that takes a FormGroup and returns a ValidationErrors object if the validation fails, or null if it passes.
     */
    static fieldsDependency(firstKey: string, secondKey: string): ValidatorFn {
        return (formGroup: FormGroup): { [key: string]: any } | null => {
            const firstField = formGroup.get(firstKey);
            const secondField = formGroup.get(secondKey);

            // Check if both fields are either filled or empty
            if ((firstField.value && secondField.value) || (!firstField.value && !secondField.value)) {
                return null; // No error
            }

            // If not, return error object
            return { fieldsDependency: true };
        };
    }

    static requiredIfValue(targetControlName: string, targetValue: any): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const form = control.parent as FormGroup;

            if (!form) return null;

            const targetControl = form.get(targetControlName);
            if (!targetControl) {
                console.warn(`Control with name "${targetControlName}" not found in the form`);
                return null;
            }

            // Si el valor del control objetivo coincide con targetValue y el control actual está vacío, mostrar error
            if (targetControl.value === targetValue && !control.value) {
                return { requiredIf: true };
            }

            return null;
        };
    }

    static requiredArrayIfValue(targetControlName: string, targetValue: unknown): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const form = control.parent as FormGroup;
            if (!form) return null;

            const targetControl = form.get(targetControlName);
            if (!targetControl) {
                console.warn(`Control with name "${targetControlName}" not found in the form`);
                return null;
            }

            if (targetControl.value !== targetValue) return null;

            const value: unknown = control.value;
            const isEmpty =
                value === null ||
                value === undefined ||
                (typeof value === "string" && value.trim() === "") ||
                (Array.isArray(value) && value.length === 0);

            return isEmpty ? { required: true } : null;
        };
    }

    static requiredIfData(targetControlName: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const form = control.parent as FormGroup;
            if (!form) {
                return null; // El formulario aún no está disponible
            }

            const targetControl = form.get(targetControlName);
            if (!targetControl) {
                return null;
            }

            // Si el control objetivo tiene un valor distinto de vacío, el control actual es requerido
            if (
                targetControl.value !== null &&
                targetControl.value !== undefined &&
                targetControl.value !== "" &&
                !control.value
            ) {
                return { required: true };
            }

            return null; // No hay error
        };
    }

    /**
     * Creates a custom validator function that checks if a control should be required based on another control's value.
     * @param targetOriginControlName The name of the control in the form whose value dictates if the validated control should be required.
     * @param targetValue The specific value of the target control that triggers the requirement for the validated control.
     * @returns A ValidatorFn, which is a function that takes an Angular AbstractControl and returns a validation result object or null.
     */
    static requiredIfAnyControlHasNoValue(
        targetOriginControlName: string,
        targetDestinyControlName: string,
    ): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            // Attempt to access the parent FormGroup of the current control.
            const form: FormGroup = control.parent as FormGroup;
            // Check if the form exists, since the control must be part of a group to evaluate its sibling's value.
            if (!form) {
                // If no form is available, there's no context to check a sibling control, hence return null (no errors).
                return null;
            }

            // Retrieve the target control using the provided name to check its value.
            const targetOriginControl = form.get(targetOriginControlName);
            const targetDestinyControl = form.get(targetDestinyControlName);

            // Determine if the target control exists and its value matches the specified target value.
            // Additionally, check if the current control has no value.

            if (
                (targetDestinyControl && targetDestinyControl.value) ||
                (targetOriginControl && targetOriginControl.value)
            ) {
                if (targetDestinyControl && targetDestinyControl.value) {
                    targetOriginControl.setErrors(null);
                }
                if (targetOriginControl && targetOriginControl.value) {
                    targetDestinyControl.setErrors(null);
                }
                // If all conditions are met, this means the validation rule is violated because the current control should have a value.
                return null; // Return an error object indicating that this control should be filled out.
            } else {
                // If the condition isn't met (either the target control's value doesn't match, or the current control has a value),
                // return null indicating there are no errors.
                return { maxAttributesOrCliks: true };
            }
        };
    }

    /**
     * Creates a custom validator function that checks if a control should be required based on another control's value.
     * @param targetControlName The name of the control in the form whose value dictates if the validated control should be required.
     * @param targetValue The specific value of the target control that triggers the requirement for the validated control.
     * @returns A ValidatorFn, which is a function that takes an Angular AbstractControl and returns a validation result object or null.
     */
    static requiredIfWithControl(formControlName: FormControl, targetValue: any): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            // Check if the target control's value does not match the specified target value.
            if (formControlName.value === targetValue && !control.value) {
                // If the target control's value matches the target value and the current control has no value,
                // return an error object indicating that this control should be filled out.
                return { required: true };
            }

            // If the condition isn't met (either the target control's value doesn't match the target value,
            // or the current control has a value), return null indicating there are no errors.
            return null;
        };
    }

    /**
     * Creates a custom validator function that checks if a control should be required based on the inverse value of another control.
     * @param targetControlName The name of the control in the form whose value determines if the validated control should not be required.
     * @param targetValue The specific value of the target control that triggers the non-requirement for the validated control.
     * @returns A ValidatorFn, which is a function that takes an Angular AbstractControl and returns a validation result object or null.
     */
    static requiredIfNot(targetControlName: string, targetValue: any): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            // Access the parent form group
            const form: FormGroup = control.parent as FormGroup;
            if (!form) return null; // If there's no form, there's nothing to validate against

            // Check the value of the target control
            const targetControl = form.get(targetControlName);

            // If the target control exists and its value does not match the specified value, and the current control has no value
            if (targetControl && targetControl.value !== targetValue && !control.value) {
                // Return validation error, as the current control should have a value when the condition is true
                return { required: true };
            }
            return null; // If the condition is not met, return no error
        };
    }

    /**
     * Static method to validate that a form control's value consists only of numbers.
     * This method returns a ValidatorFn function that can be attached to a form control.
     * It checks if the value of the form control contains only numeric characters (0-9).
     * If the value contains any non-numeric characters, the validator returns an error object
     * with a key of `onlyNumbers` set to true. Otherwise, if the value is valid or the control is empty,
     * it returns null, indicating no validation errors.
     *
     * Example usage:
     * this.formControl = new FormControl('', [FormValidator.onlyNumbers()]);
     */
    static onlyNumbers(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const numberPattern = /^[0-9]*$/;
            if (control.value && !numberPattern.test(control.value)) {
                // Return error if value contains non-numeric characters
                return { invalidBundleId: true };
            }
            return null; // Return null if there's no error
        };
    }

    /**
     * Static method to validate that a form control's value does not contain decimal numbers.
     * This method returns a ValidatorFn function that can be attached to a form control.
     * If the value contains decimal numbers, the validator returns an error object
     * with a key of `noDecimals` and a message in English. Otherwise, it returns null.
     *
     * Example usage:
     * this.formControl = new FormControl('', [FormValidator.noDecimals()]);
     */

    static noDecimals(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const numberPattern = /^[0-9]*$/;
            if (control.value && !numberPattern.test(control.value)) {
                // Return error if value contains non-numeric characters
                return { noDecimal: true };
            }
            return null; // Return null if there's no error
        };
    }

    /**
     * Validates that the control value is a non-empty array.
     * Use for any form control that must have at least one selected item (e.g. multi-select required).
     * Returns { required: true } when value is not an array or length is 0.
     */
    static nonEmptyArray(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const v = control?.value;
            if (!Array.isArray(v) || v.length === 0) {
                return { required: true };
            }
            return null;
        };
    }

    /**
     * Validates that trader_ids (or similar id array) has at least one value.
     * @deprecated Use nonEmptyArray() for generic reuse. Kept for backward compatibility.
     */
    static traderIdsRequired(): ValidatorFn {
        return FormValidator.nonEmptyArray();
    }

    /** A single full https:// URL, no whitespace inside. Surrounding whitespace is ignored. */
    static isHttpsTrackingUrl(value: string): boolean {
        return /^https:\/\/\S+$/i.test(value.trim());
    }

    /** A click URL: http:// or https:// followed by anything, as before. Only impression pixels reject inner whitespace. */
    static isHttpsOrHttpTrackingUrl(value: string): boolean {
        return /^https?:\/\/.+/i.test(value.trim());
    }

    /** Impression pixels must be a full https:// URL. Empty is valid (emptiness is handled elsewhere). */
    static httpsTrackingUrl(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return null;
            }
            return FormValidator.isHttpsTrackingUrl(value) ? null : { invalidTrackingUrl: true };
        };
    }

    /**
     * Validates a per-instance tracking click URL: MediaSmart rejects values without
     * an explicit http(s) scheme, so unlike `url()` the scheme is mandatory here.
     * Empty values pass (clearing the override is allowed).
     * Returns { invalidTrackingUrl: true } on failure.
     */
    static trackingUrl(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return null;
            }
            return FormValidator.isHttpsOrHttpTrackingUrl(value) ? null : { invalidTrackingUrl: true };
        };
    }

    /**
     * Validates that the creative name does not contain the characters | $ # or @.
     * Returns { creativeNameForbiddenChars: true } if any of these characters are present.
     */
    static creativeNameForbiddenChars(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value || typeof control.value !== "string") {
                return null;
            }
            if (/[|$#@]/.test(control.value)) {
                return { creativeNameForbiddenChars: true };
            }
            return null;
        };
    }

    /**
     * Validates password complexity: at least 8 characters, one uppercase,
     * one lowercase and one number. Empty values pass (use `required` for that).
     * On failure returns `{ passwordComplexity: PasswordChecks }` so the UI can
     * highlight exactly which rules are unmet.
     */
    static passwordComplexity(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }
            const { checks, met } = evaluatePasswordStrength(control.value);
            return met === 4 ? null : { passwordComplexity: checks };
        };
    }
}

/** The four password complexity rules and whether each is satisfied. */
export interface PasswordChecks {
    length: boolean;
    upper: boolean;
    lower: boolean;
    number: boolean;
}

/** Result of evaluating a password: the individual checks and how many pass. */
export interface PasswordStrength {
    checks: PasswordChecks;
    /** Number of satisfied checks (0..4). */
    met: number;
}

/**
 * Pure evaluation of a password against the complexity rules. Shared by the
 * `passwordComplexity` validator and the UI strength meter so both agree.
 */
export function evaluatePasswordStrength(password: string): PasswordStrength {
    const pw = password ?? "";
    const checks: PasswordChecks = {
        length: pw.length >= 8,
        upper: /[A-Z]/.test(pw),
        lower: /[a-z]/.test(pw),
        number: /[0-9]/.test(pw),
    };
    const met = Object.values(checks).filter(Boolean).length;
    return { checks, met };
}
